/**
 * Product engraving: drawer open/close, live preview, font/symbol selection,
 * and writing line item properties on save. Loaded via {% render 'vite' with 'js/engraving.js' %}
 * when engraving block is rendered (product page with engraving).
 *
 * Display keys and values are stored in the current locale so checkout shows
 * the correct language. _integration_* fields use Jeeves canonical values.
 */

/** Swedish keys for cart line item properties */
const ENGRAVING_KEYS_SV = {
  TEXT_BEFORE: "Gravyr före symbolen",
  TEXT_AFTER: "Gravyr efter symbolen",
  POSITION: "Placering av gravyr",
  FONT: "Typsnitt för gravyr",
  SYMBOL: "Symbol",
  SYMBOL_POSITION: "Placering av symbol"
};

/** English keys for cart line item properties (checkout display) */
const ENGRAVING_KEYS_EN = {
  TEXT_BEFORE: "Engraving text before",
  TEXT_AFTER: "Engraving text after",
  POSITION: "Engraving position",
  FONT: "Engraving font",
  SYMBOL: "Engraving symbol",
  SYMBOL_POSITION: "Engraving symbol position"
};

function getEngravingKeys() {
  const locale =
    (typeof window !== "undefined" && window.Shopify?.locale) ||
    document.documentElement?.getAttribute?.("lang") ||
    "sv";
  const lang = String(locale).toLowerCase().split("-")[0];
  return lang === "en" ? ENGRAVING_KEYS_EN : ENGRAVING_KEYS_SV;
}

const POPUP_SHOWN = "engraving-popup--shown";
const POPUP_HIDDEN = "engraving-popup--hidden";
const POPUP_CLOSED = "engraving-popup--closed";
const POPUP_CLOSE_MS = 280;
const HIDDEN_CLASS = "hidden";
const SYMBOL_SELECTED = "symbol-option--selected";

function createLineItem(name, value) {
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = `properties[${name}]`;
  input.value = value;
  return input;
}

function addLineItem(container, name, value) {
  if (!container || value == null || value === "") return;
  container.appendChild(createLineItem(name, String(value)));
}

function getTranslation(el) {
  return el?.getAttribute?.("data-translation") ?? el?.dataset?.translation ?? "";
}

let engravingDocBound = false;
let engravingRerenderBound = false;

function bindProductRerender() {
  if (engravingRerenderBound) return;
  engravingRerenderBound = true;
  document.addEventListener("product:rerender", () => setTimeout(initEngraving, 0), true);
  document.addEventListener("variant:change", () => setTimeout(initEngraving, 0));
}

function bindOptionButtonsDocument() {
  if (engravingDocBound) return;
  engravingDocBound = true;
  document.addEventListener(
    "click",
    (e) => {
      const popup = document.getElementById("engraving-container");
      if (e.target.closest("#engraving-close") || e.target.closest("#engraving-close-btn")) {
        if (popup) {
          popup.classList.remove(POPUP_SHOWN);
          popup.classList.add(POPUP_HIDDEN);
          popup.setAttribute("aria-hidden", "true");
          setTimeout(() => popup.classList.add(POPUP_CLOSED), POPUP_CLOSE_MS);
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const container = e.target.closest(".engraving-selection");
      if (!container) return;
      if (!popup) return;
      const engravingBtn = container.querySelector("#engraving-btn");
      const noEngravingBtn = container.querySelector("#no-engraving-btn");
      if (e.target.closest("#engraving-btn")) {
        e.preventDefault();
        e.stopPropagation();
        engravingBtn?.classList.add("is-selected");
        noEngravingBtn?.classList.remove("is-selected");
        popup.classList.remove(POPUP_CLOSED);
        popup.classList.remove(POPUP_HIDDEN);
        popup.classList.add(POPUP_SHOWN);
        popup.setAttribute("aria-hidden", "false");
        const fontSelect = popup.querySelector("#engraving-font-field");
        if (fontSelect?.options?.length) {
          const opt = fontSelect.options[fontSelect.selectedIndex];
          const fontClass = opt?.getAttribute("data-font-face") ?? "";
          [popup.querySelector("#preview-before"), popup.querySelector("#preview-after")].forEach((el) => {
            if (el) el.className = fontClass;
          });
          [
            popup.querySelector("#engraving-text-field-before"),
            popup.querySelector("#engraving-text-field-after")
          ].forEach((el) => {
            if (el) el.className = fontClass ? `input ${fontClass}` : "input";
          });
        }
      }
      if (e.target.closest("#no-engraving-btn")) {
        e.preventDefault();
        e.stopPropagation();
        noEngravingBtn?.classList.add("is-selected");
        noEngravingBtn?.classList.remove("engraving-btn__hidden");
        engravingBtn?.classList.remove("is-selected");
        popup.classList.remove(POPUP_SHOWN);
        popup.classList.add(POPUP_HIDDEN);
        popup.setAttribute("aria-hidden", "true");
        setTimeout(() => popup.classList.add(POPUP_CLOSED), POPUP_CLOSE_MS);
        if (engravingBtn) engravingBtn.textContent = engravingBtn.dataset.withEngraving ?? "";
        container.querySelector("#edit-engraving")?.classList.add(HIDDEN_CLASS);
        const formId = container.dataset.productForm;
        const form = formId ? document.getElementById(formId) : null;
        const lineItemsContainer = form?.querySelector("#engraving-line-items");
        if (lineItemsContainer) lineItemsContainer.innerHTML = "";
      }
    },
    true
  );
}

function initEngraving() {
  bindOptionButtonsDocument();

  const container = document.querySelector(".engraving-selection");
  if (!container) return;

  const popup = container.querySelector("#engraving-container");
  if (!popup) return;

  const formId = container.dataset.productForm;
  const form = formId ? document.getElementById(formId) : null;
  bindProductRerender();

  const drawerRoot = document.getElementById("engraving-drawer-root");
  if (drawerRoot) {
    drawerRoot.querySelectorAll("#engraving-container").forEach((el) => el.remove());
    drawerRoot.appendChild(popup);
  }

  if (container.dataset.engravingInited === "true") return;
  container.dataset.engravingInited = "true";

  const lineItemsContainer = form?.querySelector("#engraving-line-items");

  const editBtn = container.querySelector("#edit-engraving");
  const noEngravingBtn = container.querySelector("#no-engraving-btn");
  const engravingBtn = container.querySelector("#engraving-btn");
  const positionField = popup.querySelector("#engraving-position-field");
  const fontSelect = popup.querySelector("#engraving-font-field");
  const textBefore = popup.querySelector("#engraving-text-field-before");
  const textAfter = popup.querySelector("#engraving-text-field-after");
  const previewBefore = popup.querySelector("#preview-before");
  const previewAfter = popup.querySelector("#preview-after");
  const symbolMiddle = popup.querySelector(".engraving-symbol--middle");
  const symbolOptions = popup.querySelectorAll(".symbol-option");
  const symbolPositionMiddle = popup.querySelector("#engraving-symbol-position-middle");
  const symbolPositionBefore = popup.querySelector("#engraving-symbol-position-before");
  const symbolPositionAfter = popup.querySelector("#engraving-symbol-position-after");
  const successEl = popup.querySelector("#engraving-success");
  const successToast = popup.querySelector("#engraving-success-toast");

  let selectedSymbol = "No symbol";
  let symbolIntegrationValue = "";
  let selectedFont = "";

  if (fontSelect?.options?.length) {
    applyFontFromSelect(fontSelect);
  }

  function openDrawer() {
    popup.classList.remove(POPUP_CLOSED);
    popup.classList.remove(POPUP_HIDDEN);
    popup.classList.add(POPUP_SHOWN);
    popup.setAttribute("aria-hidden", "false");
    if (fontSelect?.options?.length) {
      applyFontFromSelect(fontSelect);
    }
  }

  function closeDrawer() {
    popup.classList.remove(POPUP_SHOWN);
    popup.classList.add(POPUP_HIDDEN);
    popup.setAttribute("aria-hidden", "true");
    setTimeout(() => popup.classList.add(POPUP_CLOSED), POPUP_CLOSE_MS);
  }

  function applyFontFromSelect(select) {
    const opt = select.options[select.selectedIndex];
    const fontClass = opt?.getAttribute("data-font-face") ?? "";
    const fontValue = opt?.getAttribute("data-font-value") ?? "";
    [previewBefore, previewAfter].forEach((el) => {
      if (el) el.className = fontClass;
    });
    [textBefore, textAfter].forEach((el) => {
      if (el) el.className = fontClass ? `input ${fontClass}` : "input";
    });
    selectedFont = fontValue;
  }

  const previewContainer = popup.querySelector("#engraving-preview");

  function updatePreview() {
    if (previewBefore) previewBefore.textContent = textBefore?.value ?? "";
    if (previewAfter) previewAfter.textContent = textAfter?.value ?? "";
    const hasContent =
      (previewBefore?.textContent?.trim() ?? "") !== "" ||
      (previewAfter?.textContent?.trim() ?? "") !== "" ||
      (symbolMiddle?.innerHTML?.trim() ?? "") !== "";
    previewContainer?.classList.toggle("engraving-preview--has-content", !!hasContent);
  }

  function setEngravingSelected() {
    engravingBtn?.classList.add("is-selected");
    noEngravingBtn?.classList.remove("is-selected");
  }

  function showEditAndNoEngraving() {
    editBtn?.classList.remove(HIDDEN_CLASS);
    editBtn?.setAttribute("aria-hidden", "false");
    noEngravingBtn?.classList.remove("engraving-btn__hidden");
  }

  function hideEditAndNoEngraving() {
    editBtn?.classList.add(HIDDEN_CLASS);
    editBtn?.setAttribute("aria-hidden", "true");
    noEngravingBtn?.classList.remove("engraving-btn__hidden");
  }

  function getCanonicalValue(el) {
    return el?.dataset?.canonicalValue ?? getTranslation(el);
  }

  function getPositionJeevesValue() {
    if (!positionField?.options?.length || !positionField.value) return null;
    const opt = positionField.options[positionField.selectedIndex];
    return opt?.dataset?.jeevesValue ?? opt?.dataset?.canonicalValue ?? positionField.value;
  }

  function buildAndWriteLineItems() {
    if (!lineItemsContainer) return;
    lineItemsContainer.innerHTML = "";

    const fontToUse =
      fontSelect?.options?.length && fontSelect.selectedIndex >= 0 && fontSelect.options[fontSelect.selectedIndex]
        ? (fontSelect.options[fontSelect.selectedIndex].getAttribute("data-font-value") ??
          fontSelect.value ??
          selectedFont)
        : selectedFont;

    const keys = getEngravingKeys();
    const vBefore = textBefore?.value?.trim() ?? "";
    const vAfter = textAfter?.value?.trim() ?? "";

    let symbolPositionDisplay = "";
    let symbolPositionCanonical = "";

    if (vBefore && !vAfter) {
      addLineItem(lineItemsContainer, keys.TEXT_BEFORE, vBefore);
      addLineItem(lineItemsContainer, "_integration_engraving_text", vBefore);
      symbolPositionDisplay = getTranslation(symbolPositionAfter);
      symbolPositionCanonical = getCanonicalValue(symbolPositionAfter);
    }
    if (!vBefore && vAfter) {
      addLineItem(lineItemsContainer, keys.TEXT_AFTER, vAfter);
      addLineItem(lineItemsContainer, "_integration_engraving_text", vAfter);
      symbolPositionDisplay = getTranslation(symbolPositionBefore);
      symbolPositionCanonical = getCanonicalValue(symbolPositionBefore);
    }
    if (vBefore && vAfter) {
      addLineItem(lineItemsContainer, keys.TEXT_BEFORE, vBefore);
      addLineItem(lineItemsContainer, "_integration_engraving_text", vBefore);
      addLineItem(lineItemsContainer, keys.TEXT_AFTER, vAfter);
      addLineItem(lineItemsContainer, "_integration_engraving_text_2", vAfter);
      symbolPositionDisplay = getTranslation(symbolPositionMiddle);
      symbolPositionCanonical = getCanonicalValue(symbolPositionMiddle);
    }
    if (positionField?.options?.length && positionField.value) {
      addLineItem(lineItemsContainer, keys.POSITION, positionField.value);
      const positionJeeves = getPositionJeevesValue();
      if (positionJeeves) {
        addLineItem(lineItemsContainer, "_integration_engraving_position", positionJeeves);
      }
    }
    if (fontToUse && fontToUse !== "default") {
      addLineItem(lineItemsContainer, keys.FONT, fontToUse);
      addLineItem(lineItemsContainer, "_integration_engraving_font", fontToUse);
    }
    if (selectedSymbol && selectedSymbol !== "No symbol") {
      addLineItem(lineItemsContainer, keys.SYMBOL, selectedSymbol);
    }
    if (symbolIntegrationValue && selectedSymbol !== "No symbol") {
      addLineItem(lineItemsContainer, "_integration_symbol_value", symbolIntegrationValue);
    }
    if (symbolPositionDisplay && selectedSymbol !== "No symbol") {
      addLineItem(lineItemsContainer, keys.SYMBOL_POSITION, symbolPositionDisplay);
    }
    if (symbolPositionCanonical && selectedSymbol !== "No symbol") {
      addLineItem(lineItemsContainer, "_integration_engraving_symbol_position", symbolPositionCanonical);
    }
  }

  function resetForm() {
    if (textBefore) textBefore.value = "";
    if (textAfter) textAfter.value = "";
    updatePreview();
    if (positionField?.options?.length) positionField.selectedIndex = 0;
    if (fontSelect?.options?.length) {
      fontSelect.selectedIndex = 0;
      applyFontFromSelect(fontSelect);
    }
    selectedSymbol = "No symbol";
    symbolIntegrationValue = "";
    if (symbolMiddle) {
      symbolMiddle.innerHTML = "";
      symbolMiddle.style.display = "";
    }
    symbolOptions?.forEach((el) => {
      el.classList.remove(SYMBOL_SELECTED, "is-selected");
      if (el.classList.contains("symbol-option--text")) {
        el.classList.add(SYMBOL_SELECTED, "is-selected");
      }
    });
  }

  if (textBefore) textBefore.addEventListener("input", updatePreview);
  if (textAfter) textAfter.addEventListener("input", updatePreview);
  if (fontSelect) {
    fontSelect.addEventListener("change", () => {
      applyFontFromSelect(fontSelect);
      updatePreview();
    });
  }

  symbolOptions?.forEach((el) => {
    el.addEventListener("click", () => {
      symbolOptions.forEach((o) => o.classList.remove(SYMBOL_SELECTED, "is-selected"));
      el.classList.add(SYMBOL_SELECTED, "is-selected");
      selectedSymbol = el.dataset.symbolName ?? "No symbol";
      symbolIntegrationValue = el.dataset.symbolValue ?? "";
      if (symbolMiddle) {
        const isNoSymbol = el.classList.contains("symbol-option--text");
        if (isNoSymbol) {
          symbolMiddle.innerHTML = "";
          symbolMiddle.style.display = "none";
        } else {
          const raw = el.getAttribute("data-symbol-svg");
          symbolMiddle.innerHTML = raw ?? "";
          symbolMiddle.style.display = "";
        }
      }
      updatePreview();
    });
    el.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        el.click();
      }
    });
  });

  popup.addEventListener("click", (e) => {
    if (e.target.closest("#engraving-save")) {
      e.preventDefault();
      e.stopPropagation();
      buildAndWriteLineItems();
      showEditAndNoEngraving();
      setEngravingSelected();
      closeDrawer();
      if (successEl) {
        successEl.classList.remove(HIDDEN_CLASS);
        setTimeout(() => successEl.classList.add(HIDDEN_CLASS), 2500);
      }
      if (successToast) {
        successToast.classList.remove(HIDDEN_CLASS);
        setTimeout(() => successToast.classList.add(HIDDEN_CLASS), 2500);
      }
    }
  });

  container.addEventListener("click", (e) => {
    if (e.target.closest("#engraving-btn")) {
      e.preventDefault();
      setEngravingSelected();
      openDrawer();
      return;
    }
    if (e.target.closest("#edit-engraving")) {
      e.preventDefault();
      openDrawer();
      return;
    }
    if (e.target.closest("#engraving-close") || e.target.closest("#engraving-close-btn")) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    if (e.target.closest("#no-engraving-btn")) {
      e.preventDefault();
      if (lineItemsContainer) lineItemsContainer.innerHTML = "";
      resetForm();
      if (engravingBtn) engravingBtn.textContent = engravingBtn.dataset.withEngraving ?? "";
      hideEditAndNoEngraving();
      noEngravingBtn?.classList.add("is-selected");
      engravingBtn?.classList.remove("is-selected");
    }
  });
}

function runEngravingInit() {
  initEngraving();
  if (document.readyState === "loading") return;
  setTimeout(initEngraving, 150);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", runEngravingInit);
} else {
  runEngravingInit();
}
